import axios from 'axios';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { RootReducerState } from '../../redux/Reducers';
import { rejectWithApiError } from '../../utils/api';
import { PaginationType } from '../../types/utils/pagination.type';
import { ServiceTicketType } from '../../types/service-calls/service-calls.type';

export type GetServiceCallReportParams = {
  page: number;
  limit: number;
  order?: string;
  q?: string;
  scheduled_from?: string;
  scheduled_to?: string;
  status?: string;
  serviceVariant?: string;
  location_ids?: string;
  customer_org_ids?: string;
  partner_org_ids?: string;
  service_organization_ids?: string;
  atRisk?: string;
  siteReadinessStatus?: string;
};

export function mergeServiceTicketReportQueryParams(
  state: RootReducerState,
  reportParams: GetServiceCallReportParams
): Record<string, unknown> {
  const merged: Record<string, unknown> = { ...reportParams };
  const selectedCompany = state.sharedReducer.selectedCompany;
  const selectedLocation = state.sharedReducer.selectedLocation;
  const includeSuborgs = state.sharedReducer.includeSuborgs;
  const user = state.authReducer.user;

  const isTelCloudUser = user?.organization?.level === 0;

  if (selectedCompany && selectedCompany.id !== 'all') {
    merged.org_id = selectedCompany.id;
  } else if (!isTelCloudUser && user?.organization?.id) {
    merged.org_id = user.organization.id;
  }

  if (selectedLocation && selectedLocation.id !== 'all' && +selectedLocation.id) {
    merged.locationId = selectedLocation.id;
  }

  if (includeSuborgs) {
    merged.inc_sub = true;
  }

  return merged;
}

async function requestServiceCallReport(
  state: RootReducerState,
  params: GetServiceCallReportParams
): Promise<PaginationType<ServiceTicketType>> {
  const merged = mergeServiceTicketReportQueryParams(state, params);

  const response = await axios.get<PaginationType<ServiceTicketType>>(
    `${process.env.REACT_APP_API_URL}/service-ticket`,
    { params: merged }
  );

  return response.data;
}

export const getServiceCallReport = createAsyncThunk<
  PaginationType<ServiceTicketType>,
  { params: GetServiceCallReportParams },
  { state: RootReducerState }
>('serviceCallReport/getReport', async ({ params }, thunkAPI) => {
  try {
    return await requestServiceCallReport(thunkAPI.getState(), params);
  } catch (error) {
    return rejectWithApiError({
      thunkAPI,
      error,
      fallback: 'Error while loading Service Call report.',
    });
  }
});

export const getServiceCallReportPage = createAsyncThunk<
  PaginationType<ServiceTicketType>,
  { params: GetServiceCallReportParams },
  { state: RootReducerState }
>('serviceCallReport/getReportPage', async ({ params }, thunkAPI) => {
  try {
    return await requestServiceCallReport(thunkAPI.getState(), params);
  } catch (error) {
    return rejectWithApiError({
      thunkAPI,
      error,
      fallback: 'Error while loading Service Call report.',
    });
  }
});
